import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import { useUserStore } from "@/stores";
import { message } from "antd";
import type { Dispatch } from "react";
import type { Action, State } from "../children/AssetsTable";
import type { FilterCriteria } from "@/layouts/signed-in/views/Locations/children/SmartFilter";

// Cache for the full dataset to avoid re-fetching on pagination
// Cache duration: 5 minutes for better performance
const CACHE_DURATION_MS = 5 * 60 * 1000;

let cachedAssets: any[] | null = null;
let lastFetchParams: string | null = null;
// Map of cache keys to fetch promises - allows multiple concurrent fetches for different queries
const fetchPromises = new Map<string, Promise<any>>();

// Function to clear the asset cache (useful for forcing fresh fetches after mutations)
// Call this after: creating, updating, or deleting assets
export const clearAssetCache = () => {
	cachedAssets = null;
	lastFetchParams = null;
	fetchPromises.clear();
};

// Helper to get nested value from an object (supports string or array paths)
const getNestedValue = (obj: any, path: string | string[]): any => {
	if (typeof path === "string") {
		// Handle dot notation like "assetType.name"
		if (path.includes(".")) {
			return path.split(".").reduce((acc, key) => acc?.[key], obj);
		}
		return obj?.[path];
	}
	return path.reduce((acc, key) => acc?.[key], obj);
};

// Apply smart filters to the data
const applySmartFilters = (
	data: any[],
	filters: FilterCriteria[],
): any[] => {
	if (!filters || filters.length === 0) {
		return data;
	}

	return data.filter((item) => {
		// All filters must match (AND logic)
		return filters.every((filter) => {
			const itemValue = getNestedValue(item, filter.column);
			const itemValueStr = itemValue != null ? String(itemValue).toLowerCase() : "";

			if (filter.operator === "in" && Array.isArray(filter.value)) {
				// Check if item value matches any of the selected values
				return filter.value.some(
					(v) => v.toLowerCase() === itemValueStr,
				);
			}
			if (filter.operator === "equals") {
				// Exact match (case-insensitive)
				const filterValueStr = String(filter.value).toLowerCase();
				return itemValueStr === filterValueStr;
			}
			if (filter.operator === "contains") {
				// Contains match (case-insensitive)
				const filterValueStr = String(filter.value).toLowerCase();
				return itemValueStr.includes(filterValueStr);
			}
			return true;
		});
	});
};

/**
 * Builds a parent/child tree from a flat asset list. Children are nested
 * under their parent's `children` array (AntD Table renders these as
 * expandable tree rows). If a child's parent isn't in the list (filtered
 * out, other page, deleted), the child is promoted to top level so it's
 * never silently hidden.
 */
const buildAssetTree = (rows: any[]): any[] => {
	const byId = new Map<number, any>();
	for (const row of rows) {
		byId.set(row.id, { ...row, children: undefined });
	}
	const roots: any[] = [];
	for (const node of byId.values()) {
		const parent =
			node.parentAssetId != null ? byId.get(node.parentAssetId) : undefined;
		if (parent) {
			if (!parent.children) parent.children = [];
			parent.children.push(node);
		} else {
			roots.push(node);
		}
	}
	return roots;
};

/** Marks rows that have children (childCount > 0) with an empty children
 * array so AntD renders an expander; actual rows are lazy-loaded on expand. */
const withLazyChildren = (rows: any[]): any[] =>
	rows.map((row: any) =>
		row.childCount > 0 && !row.children ? { ...row, children: [] } : row,
	);

export const fetchData = async (
	state: State,
	dispatch: Dispatch<Action>,
	browserLocationId: string,
) => {
	let {
		pageNum,
		pageSize,
		sortBy,
		sortOrder,
		searchVal,
		dateRange,
		filters,
		viewingDeleted,
		smartFilters,
	} = state;
	try {
		const user = useUserStore.getState().user;
		const organizationId = user?.organization?.id;

		if (!organizationId) {
			throw new Error("Organization ID not found");
		}

		// If user is searching, use server-side search to find everything
		// Otherwise use cached lightweight endpoint for fast browsing
		const isSearching = searchVal && searchVal.trim().length > 0;

		if (isSearching) {
			// Use server-side search when user is actively searching
			// This allows finding assets beyond the cached limit
			dispatch({ type: "SET_IS_LOADING", payload: true });

			// Build search filters for the API
			const searchFilters: any = {
				organizationId: [organizationId],
				searchTerm: searchVal.trim(),
			};

			// Add location filter if browsing a specific location
			if (browserLocationId) {
				searchFilters.locationId = [parseInt(browserLocationId)];
			}

			// Add deleted filter - server-side filtering
			if (viewingDeleted) {
				searchFilters.isDeleted = true;
			} else {
				searchFilters.isDeleted = false;
			}

			// Add date range filter
			if (dateRange[0] || dateRange[1]) {
				if (dateRange[0]) {
					searchFilters.startDate = dateRange[0].toISOString();
				}
				if (dateRange[1]) {
					searchFilters.endDate = dateRange[1].toISOString();
				}
			}

			// Add any additional filters from state
			if (filters && Object.keys(filters).length > 0) {
				Object.assign(searchFilters, filters);
			}

			// Map sortBy to backend field names if needed
			let backendSortBy = sortBy;
			if (sortBy === "assetTag") backendSortBy = "asset_tag";
			else if (sortBy === "serialNumber") backendSortBy = "serial_number";
			else if (sortBy === "modelNumber") backendSortBy = "model_number";
			else if (sortBy === "createdTimestamp")
				backendSortBy = "created_timestamp";
			else if (sortBy === "updatedTimestamp")
				backendSortBy = "updated_timestamp";
			else if (sortBy === "isDeleted") backendSortBy = "is_deleted";
			else if (sortBy === "asset_type_name") backendSortBy = "asset_type_name";
			else if (sortBy === "location_name") backendSortBy = "location_name";
			else if (sortBy === "manufacturer") backendSortBy = "manufacturer_name";
			else if (sortBy === "health_score") backendSortBy = "health_score";

			// Use the search endpoint with server-side pagination
			const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/search`;
			const response = await fetch(url, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					page: pageNum,
					pageSize: pageSize,
					sortBy: backendSortBy,
					sortOrder: sortOrder === "desc" ? "desc" : "asc",
					filters: searchFilters,
				}),
			});

			if (!response.ok) {
				throw new Error(`Failed to search assets (${response.status})`);
			}

			const result = await response.json();
			const searchResult = result.success ? result.data : result;

			// Update state with paginated results. Search results stay flat so
			// child assets matching the term appear directly; rows with children
			// still get an expander via lazy loading.
			dispatch({
				type: "SET_DATA_SOURCE",
				payload: withLazyChildren(searchResult.data || []),
			});
			dispatch({ type: "SET_COUNT", payload: searchResult.total || 0 });
			dispatch({ type: "SET_IS_LOADING", payload: false });
			return;
		}

		// No search - use server-side endpoint for fast browsing
		// If viewing a single location, use search endpoint to only fetch that location's assets
		// Otherwise use cached lightweight endpoint for organization-wide browsing

		if (browserLocationId) {
			// Single location view - use search endpoint with location filter
			// This ensures we only fetch assets for this specific location
			dispatch({ type: "SET_IS_LOADING", payload: true });

			const searchFilters: any = {
				organizationId: [organizationId],
				locationId: [parseInt(browserLocationId)],
				// Hide child assets from the top-level list; they're reachable by
				// expanding their parent row instead.
				rootOnly: true,
			};

			// Add deleted filter
			if (viewingDeleted) {
				searchFilters.isDeleted = true;
			} else {
				searchFilters.isDeleted = false;
			}

			// Add date range filter
			if (dateRange[0] || dateRange[1]) {
				if (dateRange[0]) {
					searchFilters.startDate = dateRange[0].toISOString();
				}
				if (dateRange[1]) {
					searchFilters.endDate = dateRange[1].toISOString();
				}
			}

			// Add any additional filters from state
			if (filters && Object.keys(filters).length > 0) {
				Object.assign(searchFilters, filters);
			}

			// Map sortBy to backend field names if needed
			let backendSortBy = sortBy;
			if (sortBy === "assetTag") backendSortBy = "asset_tag";
			else if (sortBy === "serialNumber") backendSortBy = "serial_number";
			else if (sortBy === "modelNumber") backendSortBy = "model_number";
			else if (sortBy === "createdTimestamp")
				backendSortBy = "created_timestamp";
			else if (sortBy === "updatedTimestamp")
				backendSortBy = "updated_timestamp";
			else if (sortBy === "isDeleted") backendSortBy = "is_deleted";
			else if (sortBy === "asset_type_name") backendSortBy = "asset_type_name";
			else if (sortBy === "location_name") backendSortBy = "location_name";
			else if (sortBy === "manufacturer") backendSortBy = "manufacturer_name";
			else if (sortBy === "health_score") backendSortBy = "health_score";

			// Use the search endpoint with location filter
			const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/search`;
			const response = await fetch(url, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					page: pageNum,
					pageSize: pageSize,
					sortBy: backendSortBy,
					sortOrder: sortOrder === "desc" ? "desc" : "asc",
					filters: searchFilters,
				}),
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch location assets (${response.status})`);
			}

			const result = await response.json();
			const searchResult = result.success ? result.data : result;

			// Update state with paginated results (children lazy-load on expand)
			dispatch({
				type: "SET_DATA_SOURCE",
				payload: withLazyChildren(searchResult.data || []),
			});
			dispatch({ type: "SET_COUNT", payload: searchResult.total || 0 });
			dispatch({ type: "SET_IS_LOADING", payload: false });
			return;
		}

		// Organization-wide view - use cached lightweight endpoint
		// Create cache key (not including pagination, search, sorting, or viewingDeleted)
		const cacheKey = `${organizationId}`;

		let allAssets: any[] | undefined = undefined;

		// Check if we have valid cached data first
		if (
			cachedAssets &&
			lastFetchParams === cacheKey &&
			(Date.now() - (cachedAssets as any).timestamp || 0) < CACHE_DURATION_MS
		) {
			allAssets = cachedAssets;
			// No loading spinner for cached data
		}

		// If no cached data, check if a fetch is already in progress for this cache key
		const existingPromise = fetchPromises.get(cacheKey);
		if (!allAssets && existingPromise) {
			try {
				// Wait for the ongoing fetch to complete
				allAssets = await existingPromise;
			} catch (error) {
				// If the ongoing fetch failed, we'll try again below
				console.warn("[AssetsTable] Ongoing fetch failed, will retry:", error);
			}
		}

		// Only fetch if we don't have cached data and no fetch is in progress for this key
		if (!allAssets && !fetchPromises.has(cacheKey)) {
			// Show loading for fresh data fetch
			dispatch({ type: "SET_IS_LOADING", payload: true });

			// Create a new fetch promise for this cache key
			const newFetchPromise = (async () => {
				try {
					// Always fetch ALL assets (deleted + non-deleted) using lightweight mode
					// Lightweight mode excludes photos and generative AI data
					const url = `${VITE_CORE_API_URL}${CORE_URL}/asset/organization/${organizationId}?include_deleted=true&lightweight=true`;
					const response = await fetch(url, {
						method: "GET",
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
							"Content-Type": "application/json",
						},
					});

					if (!response.ok) {
						throw new Error(`Failed to fetch assets (${response.status})`);
					}

					const result = await response.json();
					let fetchedAssets = result.success ? result.data : result;

					// Transform flat structure to nested structure expected by table columns
					if (Array.isArray(fetchedAssets)) {
						fetchedAssets = fetchedAssets.map((asset: any) => ({
							...asset,
							// Transform flat fields to nested objects
							location: {
								id: asset.locationId,
								name: asset.locationName || "",
								address1: asset.address1 || "",
								city: asset.city || "",
								state: asset.state || "",
							},
							assetType: {
								id: asset.assetTypeId,
								name: asset.assetTypeName || "",
							},
							manufacturer: {
								id: asset.manufacturerId,
								name: asset.manufacturerName || "",
							},
							healthScore:
								asset.healthScore != null
									? {
											healthScore: asset.healthScore,
										}
									: undefined,
							warranty: { hasWarranty: asset.hasWarranty === true },
						}));
					}

					// Cache the fresh data (all assets, deleted + non-deleted)
					if (fetchedAssets) {
						cachedAssets = fetchedAssets;
						lastFetchParams = cacheKey;
						(cachedAssets as any).timestamp = Date.now();
					}

					return fetchedAssets;
				} finally {
					// Clear the promise when done (success or failure)
					fetchPromises.delete(cacheKey);
				}
			})();

			// Store the promise so other calls can wait for it
			fetchPromises.set(cacheKey, newFetchPromise);

			// Wait for our own fetch to complete
			allAssets = await newFetchPromise;
		}

		// Apply client-side filtering and sorting
		// We always have all assets (deleted + non-deleted) in cache, so filter based on viewingDeleted state
		if (Array.isArray(allAssets)) {
			// Create filtered copy to avoid mutating cached data
			let filteredAssets = allAssets;

			// Apply deleted filter based on viewingDeleted state
			if (viewingDeleted) {
				// Show only deleted assets (isDeleted should be a timestamp string, not null/undefined)
				filteredAssets = filteredAssets.filter((asset: any) => {
					return asset.isDeleted != null;
				});
			} else {
				// Show only non-deleted assets
				filteredAssets = filteredAssets.filter((asset: any) => {
					return asset.isDeleted == null;
				});
			}

			// Apply search filter
			if (searchVal) {
				const searchLower = searchVal.toLowerCase();
				filteredAssets = filteredAssets.filter((asset: any) => {
					return (
						asset.assetTag?.toLowerCase().includes(searchLower) ||
						asset.assetType?.name?.toLowerCase().includes(searchLower) ||
						asset.manufacturer?.name?.toLowerCase().includes(searchLower) ||
						asset.modelNumber?.toLowerCase().includes(searchLower) ||
						asset.serialNumber?.toLowerCase().includes(searchLower) ||
						asset.location?.name?.toLowerCase().includes(searchLower) ||
						asset.location?.address1?.toLowerCase().includes(searchLower)
					);
				});
			}

			// Note: Location filter not needed here since we already filtered at API level
			// if viewing a single location (handled above)

			// Apply date range filter
			if (dateRange[0] || dateRange[1]) {
				filteredAssets = filteredAssets.filter((asset: any) => {
					const assetDate = new Date(asset.createdTimestamp);
					const startDate = dateRange[0];
					const endDate = dateRange[1];

					if (startDate && assetDate < startDate) return false;
					if (endDate && assetDate > endDate) return false;
					return true;
				});
			}

			// Apply smart filters (column-based advanced filters)
			if (smartFilters && smartFilters.length > 0) {
				filteredAssets = applySmartFilters(filteredAssets, smartFilters);
			}

			// Apply sorting
			if (sortBy) {
				// Map column keys to their actual property paths
				const sortByMap: Record<string, string> = {
					asset_type_name: "assetType.name",
					location_name: "location.name",
					manufacturer: "manufacturer.name",
					health_score: "healthScore.healthScore",
					asset_type_category: "assetTypeCategory.name",
				};

				// Use mapped property path if available, otherwise use sortBy as-is
				const propertyPath = sortByMap[sortBy] || sortBy;

				filteredAssets.sort((a: any, b: any) => {
					let aVal = a[propertyPath];
					let bVal = b[propertyPath];

					// Handle nested properties
					if (propertyPath.includes(".")) {
						const keys = propertyPath.split(".");
						aVal = keys.reduce((obj, key) => obj?.[key], a);
						bVal = keys.reduce((obj, key) => obj?.[key], b);
					}

					// Handle null/undefined values
					if (aVal == null && bVal == null) return 0;
					if (aVal == null) return 1;
					if (bVal == null) return -1;

					// Convert to strings for comparison
					const aStr = String(aVal).toLowerCase();
					const bStr = String(bVal).toLowerCase();

					const comparison = aStr.localeCompare(bStr);
					return sortOrder === "desc" ? -comparison : comparison;
				});
			}

			// Nest children under their parents as expandable tree rows when
			// browsing. During search the list stays flat so matching children
			// are directly visible.
			const displayAssets = searchVal
				? filteredAssets
				: buildAssetTree(filteredAssets);

			// Apply pagination (over top-level rows; children ride along with
			// their parent row)
			const total = displayAssets.length;
			const startIndex = (pageNum - 1) * pageSize;
			const endIndex = startIndex + pageSize;
			const paginatedAssets = displayAssets.slice(startIndex, endIndex);

			// Handle pagination reset if no results on current page
			// Only reset if we truly have no data for this page AND there's data available
			if (paginatedAssets.length === 0 && pageNum > 1 && total > 0) {
				// This means the user is on a page that doesn't exist
				// Calculate the last valid page
				const maxPage = Math.ceil(total / pageSize);
				const targetPage = Math.min(pageNum, maxPage);

				if (targetPage !== pageNum) {
					// Only reset if we actually need to change pages
					const newStartIndex = (targetPage - 1) * pageSize;
					const newEndIndex = newStartIndex + pageSize;
					const newPaginatedAssets = displayAssets.slice(
						newStartIndex,
						newEndIndex,
					);

					dispatch({ type: "SET_PAGE_NUM", payload: targetPage });
					dispatch({ type: "SET_DATA_SOURCE", payload: newPaginatedAssets });
					dispatch({ type: "SET_COUNT", payload: total });
					message.info(
						`Page ${pageNum} doesn't exist. Showing page ${targetPage}.`,
					);
					return; // Exit early to avoid double dispatch
				}
			}

			// Normal case - just update the data
			dispatch({ type: "SET_DATA_SOURCE", payload: paginatedAssets });
			dispatch({ type: "SET_COUNT", payload: total });
		} else {
			// Handle case where response is not an array
			dispatch({ type: "SET_DATA_SOURCE", payload: [] });
			dispatch({ type: "SET_COUNT", payload: 0 });
		}
	} catch (exception) {
		console.error(`Failed to fetch organization assets: ${exception}`);
		message.error("Failed to load assets. Please try again.");
		dispatch({ type: "SET_DATA_SOURCE", payload: [] });
		dispatch({ type: "SET_COUNT", payload: 0 });
	} finally {
		dispatch({ type: "SET_IS_LOADING", payload: false });
	}
};
